import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, FileBarChart2, Sparkles, Lightbulb, Bell, Settings, CreditCard, X, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Persistent left sidebar for authed `app` pages.
 *
 * Behavior:
 *   • lg+    : always visible (sticky), 240px wide.
 *   • below  : controlled by parent via `mobileOpen` (overlay/drawer).
 *
 * Active-state is derived from the current route. Unread bell badge polls
 * `/api/notifications` once on mount + every 60s — matches NotificationBell.
 */
// PR: delayed-paywall. requiresOnboarded items show a lock icon and route
// to /dashboard (where the start-onboarding hero lives) until the user
// finishes Step 3. Dashboard/Settings/Notifications/Pricing stay open —
// every authed user can manage their account.
const ITEMS = [
    { to: "/dashboard",       icon: Home,          label: "Кабінет" },
    { to: "/reports",         icon: FileBarChart2, label: "Звіти",                requiresOnboarded: true },
    { to: "/strategy",        icon: Sparkles,      label: "Стратегія",            requiresOnboarded: true },
    { to: "/recommendations", icon: Lightbulb,     label: "Рекомендації Google",  requiresOnboarded: true },
    { to: "/notifications",   icon: Bell,          label: "Сповіщення",           badgeKey: "unread_notifs" },
    { to: "/settings",        icon: Settings,      label: "Налаштування" },
    { to: "/pricing",         icon: CreditCard,    label: "Тарифи" },
];

export default function AppSidebar({ mobileOpen, onClose }) {
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [unread, setUnread] = React.useState(0);
    const onboarded = user?.onboarding_step === "done";

    React.useEffect(() => {
        let cancelled = false;
        const load = () => api.get("/notifications?limit=1")
            .then(r => { if (!cancelled) setUnread(r.data?.unread || 0); })
            .catch(() => {});
        load();
        const t = setInterval(load, 60_000);
        return () => { cancelled = true; clearInterval(t); };
    }, []);

    const Content = (
        <nav className="flex flex-col h-full" data-testid="app-sidebar">
            <ul className="flex-1 px-3 py-4 space-y-0.5">
                {ITEMS.map(item => {
                    const active = location.pathname === item.to ||
                                   location.pathname.startsWith(item.to + "/");
                    const Icon = item.icon;
                    const badge = item.badgeKey === "unread_notifs" && unread > 0 ? unread : null;
                    const locked = item.requiresOnboarded && !onboarded;

                    // Locked items don't navigate — they redirect to /dashboard
                    // where the onboarding hero lives. We still render them so
                    // the user can SEE what they'll unlock, just dimmed with
                    // a lock icon and a tooltip-style title.
                    const handleClick = (e) => {
                        if (locked) {
                            e.preventDefault();
                            onClose?.();
                            navigate("/dashboard");
                        } else {
                            onClose?.();
                        }
                    };

                    return (
                        <li key={item.to}>
                            <Link
                                to={locked ? "/dashboard" : item.to}
                                onClick={handleClick}
                                data-testid={`sidebar-${item.to.slice(1)}${locked ? "-locked" : ""}`}
                                title={locked ? "Завершіть онбординг щоб розблокувати" : undefined}
                                className={`group relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition border-l-2 ${
                                    active
                                        ? "bg-primary/10 text-primary-c border-primary"
                                        : locked
                                            ? "text-faint-c hover:text-secondary-c hover:bg-surface-hover/20 border-transparent cursor-pointer"
                                            : "text-secondary-c hover:text-text-primary hover:bg-surface-hover/30 border-transparent"
                                }`}
                            >
                                <Icon size={16} />
                                <span className="flex-1">{item.label}</span>
                                {locked && (
                                    <Lock size={12} className="text-faint-c" aria-label="locked" />
                                )}
                                {badge != null && !locked && (
                                    <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-error text-white text-[10px] font-mono font-bold flex items-center justify-center" data-testid="sidebar-notifs-badge">
                                        {badge > 9 ? "9+" : badge}
                                    </span>
                                )}
                            </Link>
                        </li>
                    );
                })}
            </ul>

        </nav>
    );

    return (
        <>
            {/* Desktop — always visible */}
            <aside className="hidden lg:flex w-[240px] shrink-0 border-r border-border bg-surface/20 sticky top-[68px] h-[calc(100vh-68px)] overflow-y-auto" data-testid="sidebar-desktop">
                {Content}
            </aside>

            {/* Mobile/Tablet drawer */}
            {mobileOpen && (
                <div className="lg:hidden fixed inset-0 z-50" data-testid="sidebar-drawer-overlay">
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
                    <aside className="relative w-[280px] h-full bg-background border-r border-border flex flex-col">
                        <div className="flex items-center justify-end h-[68px] px-3 border-b border-border">
                            <button onClick={onClose} className="p-2 text-secondary-c hover:text-text-primary" data-testid="sidebar-drawer-close">
                                <X size={18} />
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto">{Content}</div>
                    </aside>
                </div>
            )}
        </>
    );
}